import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMagnifyingGlass, faPlus, faStar, faFire, faPen, faTrash,
    faBorderAll, faList, faChevronLeft, faChevronRight, faWineGlass,
} from '@fortawesome/free-solid-svg-icons';
import { togglePromocionProducto, deleteProductos } from './services/index';
import { formatPrice } from "../utils/formatPrice";
import './adminStyles.css';

const AdmProductos = ({ productos, categorias, onProductUpdate, onActualizarProducto, onAgregarProducto }) => {
    const [viewMode, setViewMode] = useState('grid');
    const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('');
    const [mostrarDestacados, setMostrarDestacados] = useState(false);
    const [mostrarPromocionados, setMostrarPromocionados] = useState(false);
    const [busqueda, setBusqueda] = useState('');

    const [deleteModal, setDeleteModal] = useState(null);
    const [promoModal, setPromoModal] = useState(null);
    const [precioPromo, setPrecioPromo] = useState('');
    const [promoLoading, setPromoLoading] = useState(false);

    const productosFiltrados = productos.filter((p) => {
        const coincideCategoria = categoriaSeleccionada ? p.categoria?._id === categoriaSeleccionada : true;
        const coincideDestacado = mostrarDestacados ? p.destacado === true : true;
        const coincideBusqueda = busqueda
            ? p.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
              p.tipoWhisky.toLowerCase().includes(busqueda.toLowerCase()) ||
              p.descripcion.toLowerCase().includes(busqueda.toLowerCase())
            : true;
        const coincidePromocion = mostrarPromocionados ? p.enPromocion === true : true;
        return coincideCategoria && coincideDestacado && coincideBusqueda && coincidePromocion;
    });

    const [paginaActual, setPaginaActual] = useState(1);
    const productosPorPagina = 12;
    const totalPaginas = Math.ceil(productosFiltrados.length / productosPorPagina);
    const productosPaginados = productosFiltrados.slice(
        (paginaActual - 1) * productosPorPagina,
        paginaActual * productosPorPagina
    );

    useEffect(() => {
        setPaginaActual(1);
    }, [categoriaSeleccionada, mostrarDestacados, busqueda, mostrarPromocionados]);

    const toggleDestacado = async (id) => {
        try {
            const token = localStorage.getItem('token');
            const baseUrl = process.env.REACT_APP_BASE_URL;
            await axios.put(`${baseUrl}/productos/${id}/destacado`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            onProductUpdate();
        } catch (error) {
            console.error('Error al cambiar destacado:', error);
        }
    };

    const confirmarEliminar = async () => {
        try {
            await deleteProductos(deleteModal._id);
            setDeleteModal(null);
            onProductUpdate();
        } catch (error) {
            console.error('Error al eliminar producto:', error);
        }
    };

    const abrirPromoModal = (producto) => {
        setPromoModal(producto);
        setPrecioPromo(producto.precioPromocion ? String(producto.precioPromocion) : '');
    };

    const confirmarPromo = async () => {
        if (!promoModal) return;
        const nuevoEstado = !promoModal.enPromocion;
        if (nuevoEstado && (!precioPromo || isNaN(parseFloat(precioPromo)))) return;

        setPromoLoading(true);
        try {
            await togglePromocionProducto(promoModal._id, nuevoEstado, nuevoEstado ? parseFloat(precioPromo) : null);
            onProductUpdate();
            setPromoModal(null);
        } catch (error) {
            console.error('Error al actualizar promoción:', error);
        } finally {
            setPromoLoading(false);
        }
    };

    return (
        <div>
            <header className="admin-topbar">
                <div className="admin-topbar-brand">
                    <span className="admin-topbar-eyebrow">Panel de administración</span>
                    <span className="admin-topbar-title">Whisky & Co</span>
                </div>
                <div className="admin-topbar-right">
                    <button className="admin-btn-primary" onClick={onAgregarProducto}>
                        <FontAwesomeIcon icon={faPlus} /> Nuevo Producto
                    </button>
                </div>
            </header>

            <div className="admin-content">
                <div className="admin-filterbar">
                    <div className="admin-search-wrap">
                        <FontAwesomeIcon icon={faMagnifyingGlass} className="admin-search-icon" />
                        <input
                            className="admin-input"
                            type="text"
                            placeholder="Buscar por nombre, tipo o descripción..."
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                        />
                    </div>

                    <select
                        className="admin-select"
                        value={categoriaSeleccionada}
                        onChange={(e) => setCategoriaSeleccionada(e.target.value)}
                    >
                        <option value="">Todas las categorías</option>
                        {categorias.map((cat) => (
                            <option key={cat._id} value={cat._id}>{cat.nombre}</option>
                        ))}
                    </select>

                    <div className="admin-filter-pills">
                        <button
                            className={`admin-pill ${mostrarDestacados ? 'active' : ''}`}
                            onClick={() => setMostrarDestacados((p) => !p)}
                        >
                            <FontAwesomeIcon icon={faStar} /> Destacados
                        </button>
                        <button
                            className={`admin-pill ${mostrarPromocionados ? 'active' : ''}`}
                            onClick={() => setMostrarPromocionados((p) => !p)}
                        >
                            <FontAwesomeIcon icon={faFire} /> En promo
                        </button>
                    </div>

                    <div className="admin-view-toggle">
                        <button
                            className={`admin-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                            onClick={() => setViewMode('grid')}
                            title="Vista grilla"
                        ><FontAwesomeIcon icon={faBorderAll} /></button>
                        <button
                            className={`admin-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                            onClick={() => setViewMode('list')}
                            title="Vista lista"
                        ><FontAwesomeIcon icon={faList} /></button>
                    </div>
                </div>

                <div className="admin-results-bar">
                    <span className="admin-results-count">
                        {productosFiltrados.length} producto{productosFiltrados.length !== 1 ? 's' : ''}
                    </span>
                </div>

                {productosPaginados.length === 0 ? (
                    <div className="admin-empty">
                        <div className="admin-empty-icon">
                            <FontAwesomeIcon icon={faWineGlass} />
                        </div>
                        <p className="admin-empty-text">No se encontraron productos</p>
                    </div>
                ) : viewMode === 'grid' ? (
                    <div className="admin-grid">
                        {productosPaginados.map((p) => (
                            <ProductCard
                                key={p._id}
                                producto={p}
                                onToggleDestacado={toggleDestacado}
                                onPromo={abrirPromoModal}
                                onEditar={onActualizarProducto}
                                onEliminar={setDeleteModal}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="admin-list">
                        {productosPaginados.map((p) => (
                            <ProductRow
                                key={p._id}
                                producto={p}
                                onToggleDestacado={toggleDestacado}
                                onPromo={abrirPromoModal}
                                onEditar={onActualizarProducto}
                                onEliminar={setDeleteModal}
                            />
                        ))}
                    </div>
                )}

                {totalPaginas > 1 && (
                    <div className="admin-pagination">
                        <button
                            className="admin-page-btn"
                            disabled={paginaActual === 1}
                            onClick={() => { setPaginaActual((p) => p - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        ><FontAwesomeIcon icon={faChevronLeft} /> Anterior</button>
                        <span className="admin-page-info">Página {paginaActual} de {totalPaginas}</span>
                        <button
                            className="admin-page-btn"
                            disabled={paginaActual === totalPaginas}
                            onClick={() => { setPaginaActual((p) => p + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        >Siguiente <FontAwesomeIcon icon={faChevronRight} /></button>
                    </div>
                )}
            </div>

            {deleteModal && (
                <div className="admin-modal-overlay" onClick={() => setDeleteModal(null)}>
                    <div className="admin-modal admin-modal-sm" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-confirm-modal">
                            {deleteModal.imagen && (
                                <img src={deleteModal.imagen} alt={deleteModal.nombre} className="admin-confirm-img" />
                            )}
                            <h2 className="admin-confirm-title">¿Eliminar producto?</h2>
                            <p className="admin-confirm-text">
                                Estás por eliminar <strong>{deleteModal.nombre}</strong> de forma permanente. Esta acción no se puede deshacer.
                            </p>
                            <div className="admin-confirm-actions">
                                <button className="admin-btn-ghost" onClick={() => setDeleteModal(null)}>Cancelar</button>
                                <button className="admin-btn-danger" onClick={confirmarEliminar}>Eliminar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {promoModal && (
                <div className="admin-modal-overlay" onClick={() => setPromoModal(null)}>
                    <div className="admin-modal admin-modal-sm" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-confirm-modal">
                            <h2 className="admin-confirm-title">
                                {promoModal.enPromocion ? 'Desactivar promo' : 'Activar promo'}
                            </h2>
                            <p className="admin-confirm-text">
                                {promoModal.enPromocion
                                    ? `¿Desactivar la promoción de ${promoModal.nombre}?`
                                    : `Ingresá el precio de promoción para ${promoModal.nombre}`}
                            </p>

                            {!promoModal.enPromocion && (
                                <div className="admin-promo-input-wrap">
                                    <label className="admin-promo-label">Precio promocional ($)</label>
                                    <input
                                        className="admin-form-input"
                                        type="number"
                                        placeholder="Ej: 15000"
                                        value={precioPromo}
                                        onChange={(e) => setPrecioPromo(e.target.value)}
                                        autoFocus
                                        onKeyDown={(e) => e.key === 'Enter' && confirmarPromo()}
                                    />
                                </div>
                            )}

                            <div className="admin-confirm-actions">
                                <button className="admin-btn-ghost" onClick={() => setPromoModal(null)}>Cancelar</button>
                                <button
                                    className="admin-btn-primary"
                                    onClick={confirmarPromo}
                                    disabled={promoLoading || (!promoModal.enPromocion && !precioPromo)}
                                >
                                    {promoLoading ? '...' : promoModal.enPromocion ? 'Desactivar' : 'Activar promo'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const ProductCard = ({ producto, onToggleDestacado, onPromo, onEditar, onEliminar }) => {
    const { _id, nombre, descripcion, categoria, precio, imagen, tipoWhisky, destacado, enPromocion, precioPromocion } = producto;

    return (
        <div className="admin-card">
            <div className="admin-card-img-wrap">
                <img src={imagen} alt={nombre} className="admin-card-img" />
                <div className="admin-card-badges">
                    {destacado && <span className="admin-badge admin-badge--destacado">⭐ Destacado</span>}
                    {enPromocion && <span className="admin-badge admin-badge--promo">🔥 Promo</span>}
                </div>
            </div>
            <div className="admin-card-body">
                <p className="admin-card-name">{nombre}</p>
                <p className="admin-card-meta">{tipoWhisky} · {categoria?.nombre || 'Sin categoría'}</p>
                <p className="admin-card-price">{formatPrice(precio)}</p>
                {enPromocion && precioPromocion && (
                    <p className="admin-card-promo-price">
                        Promo: ${parseFloat(precioPromocion).toLocaleString('es-AR')}
                    </p>
                )}
            </div>
            <div className="admin-card-actions">
                <button
                    className={`admin-card-action-btn ${destacado ? 'active-star' : ''}`}
                    onClick={() => onToggleDestacado(_id)}
                    title={destacado ? 'Quitar destacado' : 'Marcar destacado'}
                ><FontAwesomeIcon icon={faStar} /></button>
                <button
                    className={`admin-card-action-btn ${enPromocion ? 'active-promo' : ''}`}
                    onClick={() => onPromo(producto)}
                    title={enPromocion ? 'Gestionar promo' : 'Activar promo'}
                ><FontAwesomeIcon icon={faFire} /></button>
                <button
                    className="admin-card-action-btn"
                    onClick={() => onEditar({ _id, nombre, descripcion, categoria, precio, imagen, tipoWhisky, destacado })}
                    title="Editar producto"
                ><FontAwesomeIcon icon={faPen} /></button>
                <button
                    className="admin-card-action-btn danger"
                    onClick={() => onEliminar({ _id, nombre, imagen })}
                    title="Eliminar producto"
                ><FontAwesomeIcon icon={faTrash} /></button>
            </div>
        </div>
    );
};

const ProductRow = ({ producto, onToggleDestacado, onPromo, onEditar, onEliminar }) => {
    const { _id, nombre, descripcion, categoria, precio, imagen, tipoWhisky, destacado, enPromocion, precioPromocion } = producto;

    return (
        <div className="admin-list-row">
            <img src={imagen} alt={nombre} className="admin-list-thumb" />
            <div className="admin-list-info">
                <p className="admin-list-name">{nombre}</p>
                <p className="admin-list-sub">{categoria?.nombre || 'Sin categoría'}</p>
            </div>
            <span className="admin-list-type">{tipoWhisky}</span>
            <span className="admin-list-price">{formatPrice(precio)}</span>
            <div className="admin-list-badges">
                {destacado && <span className="admin-badge admin-badge--destacado">⭐</span>}
                {enPromocion && <span className="admin-badge admin-badge--promo">🔥</span>}
            </div>
            <div className="admin-list-actions">
                <button
                    className={`admin-list-action-btn ${destacado ? 'active-star' : ''}`}
                    onClick={() => onToggleDestacado(_id)}
                    title={destacado ? 'Quitar destacado' : 'Marcar destacado'}
                ><FontAwesomeIcon icon={faStar} /></button>
                <button
                    className={`admin-list-action-btn ${enPromocion ? 'active-promo' : ''}`}
                    onClick={() => onPromo(producto)}
                    title="Gestionar promo"
                ><FontAwesomeIcon icon={faFire} /></button>
                <button
                    className="admin-list-action-btn"
                    onClick={() => onEditar({ _id, nombre, descripcion, categoria, precio, imagen, tipoWhisky, destacado })}
                    title="Editar"
                ><FontAwesomeIcon icon={faPen} /></button>
                <button
                    className="admin-list-action-btn danger"
                    onClick={() => onEliminar({ _id, nombre, imagen })}
                    title="Eliminar"
                ><FontAwesomeIcon icon={faTrash} /></button>
            </div>
        </div>
    );
};

export default AdmProductos;
